import { Text } from '@wartek-id/text'

import type { ICeritaPraktikRateValue } from 'types/cerita-praktik'
import type { FC } from 'react'

export interface IRateOption {
  label: string
  value: ICeritaPraktikRateValue
  icon: any
  selected?: boolean
}

export type IRateOptions = IRateOption[]

interface RateProps {
  title: string
  description: string
  options: IRateOptions
  className?: string
  isSubmitting: boolean
  onSelect?: (option: IRateOption) => void
}

const Rate: FC<RateProps> = ({
  title,
  description,
  options,
  className = '',
  isSubmitting = false,
  onSelect = () => {},
}) => {
  const testIdPrefix = 'cp-rate'
  let optionClassNames =
    'flex flex-col items-center space-y-1 p-2 select-none w-[136px] rounded-[16px]'
  const optionSelectedClassNames = 'bg-[#F9E8C7]'

  // If the rating is currently submitting
  if (isSubmitting) {
    optionClassNames = `${optionClassNames} opacity-50 cursor-progress`
  }

  // Other states
  else {
    optionClassNames = `${optionClassNames} cursor-pointer hover:bg-[#F9E8C7]`
  }

  // Show nothing if no options are provided
  if (Object.keys(options).length === 0) return null

  return (
    <div className={className} data-testid={testIdPrefix}>
      <div className="bg-orange-5 rounded-lg p-3 flex flex-col items-center space-y-2">
        {/* Title */}
        <Text variant="heading-sm" data-testid={`${testIdPrefix}-title`}>
          {title}
        </Text>

        {/* Options */}
        <div
          className="w-full flex flex-row justify-center space-x-2"
          data-testid={`${testIdPrefix}-options`}
        >
          {Object.values(options).map((option, index) => {
            return (
              <button
                key={`rating-opts-${index}`}
                data-testid={`${testIdPrefix}-option-${option.value}`}
                className={`${optionClassNames} ${
                  option.selected ? optionSelectedClassNames : ''
                }`}
                type="button"
                onClick={() => {
                  if (!isSubmitting) {
                    onSelect(option)
                  }
                }}
                title={option.label}
              >
                {/* Option Icon */}
                <div
                  data-testid={`${testIdPrefix}-option-${option.value}-icon`}
                >
                  {option.icon}
                </div>

                {/* Option Label */}
                <Text
                  variant="helper-bold"
                  className="mt-1"
                  data-testid={`${testIdPrefix}-option-${option.value}-label`}
                >
                  {option.label}
                </Text>
              </button>
            )
          })}
        </div>

        {/* Description */}
        <Text
          variant="body-sm"
          className="mt-2"
          data-testid={`${testIdPrefix}-description`}
        >
          {description}
        </Text>
      </div>
    </div>
  )
}

export default Rate
